import { ChevronRight, FileSignature, FileText, FolderKanban, HandCoins, Landmark, Package, Receipt, Truck, Users, type LucideIcon } from 'lucide-react';
import { SEARCH_RESULT_TYPES, type SearchResult, type SearchResultType } from '@fint/validation';
import { ColourDot } from '@/features/common/ui';
import { cn } from '@/lib/cn';

const TYPE_META: Record<SearchResultType, { icon: LucideIcon; label: string; tone: string }> = {
  invoice: { icon: FileText, label: 'Invoices', tone: 'text-blue-600 dark:text-blue-400' },
  proforma: { icon: FileSignature, label: 'Proformas', tone: 'text-indigo-600 dark:text-indigo-400' },
  customer: { icon: Users, label: 'Customers', tone: 'text-emerald-600 dark:text-emerald-400' },
  vendor: { icon: Truck, label: 'Vendors', tone: 'text-amber-600 dark:text-amber-400' },
  payment: { icon: HandCoins, label: 'Payments', tone: 'text-green-600 dark:text-green-400' },
  expense: { icon: Receipt, label: 'Expenses', tone: 'text-rose-600 dark:text-rose-400' },
  bank_transaction: { icon: Landmark, label: 'Bank lines', tone: 'text-cyan-600 dark:text-cyan-400' },
  item: { icon: Package, label: 'Items', tone: 'text-violet-600 dark:text-violet-400' },
  project: { icon: FolderKanban, label: 'Projects', tone: 'text-muted-foreground' },
};

/**
 * The grouped results and the three states before there are any - shared by
 * the panel on a large screen and the full-screen sheet on a phone. `dense`
 * is the large-screen sizing; on a phone every row is a bigger target.
 */
export function SearchResultsList({
  groups,
  flat,
  highlight,
  onPick,
  query,
  debounced,
  isFetching,
  manyBooks,
  amount,
  dense = true,
}: {
  groups: Record<SearchResultType, SearchResult[]>;
  flat: SearchResult[];
  highlight: number;
  onPick: (r: SearchResult) => void;
  query: string;
  debounced: string;
  isFetching: boolean;
  manyBooks: boolean;
  amount: string | null | undefined;
  dense?: boolean;
}) {
  if (debounced.length < 2) {
    return (
      <p className="px-3 py-3 text-xs text-muted-foreground">
        {query.trim().length === 0
          ? 'Search invoices, customers, vendors, payments, expenses, bank lines and items - by number, name, GSTIN, reference or amount.'
          : 'Keep typing…'}
      </p>
    );
  }
  if (isFetching && flat.length === 0) return <p className="px-3 py-3 text-xs text-muted-foreground">Searching…</p>;
  if (flat.length === 0) {
    return (
      <p className="px-3 py-3 text-xs text-muted-foreground" data-testid="search-empty">
        No matches for “{debounced}”.
      </p>
    );
  }
  let index = 0;
  return (
    <div data-testid="search-results">
      {amount ? (
        <p className="px-3 pt-2 text-[11px] text-muted-foreground">Includes anything of exactly {amount}.</p>
      ) : null}
      {SEARCH_RESULT_TYPES.map((type) => {
        const rows = groups[type];
        if (!rows.length) return null;
        const meta = TYPE_META[type];
        const Icon = meta.icon;
        return (
          <div key={type} className="border-b border-border last:border-b-0" data-testid={`search-group-${type}`}>
            <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{meta.label}</p>
            {rows.map((r) => {
              const at = index++;
              return (
                <button
                  key={`${r.orgId}:${r.id}`}
                  type="button"
                  onClick={() => onPick(r)}
                  data-testid="search-result"
                  data-active={at === highlight || undefined}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 text-left hover:bg-accent touch:min-h-11',
                    dense ? 'py-2' : 'py-3',
                    at === highlight && 'bg-accent',
                  )}
                >
                  <Icon className={cn('size-4 shrink-0', meta.tone)} aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{r.label}</span>
                    {r.secondary || manyBooks ? (
                      <span className="flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground">
                        {manyBooks ? (
                          <span className="inline-flex shrink-0 items-center gap-1" data-testid="search-result-book">
                            <ColourDot colour={r.orgColour} className="h-1.5 w-1.5" />
                            {r.orgName}
                            {r.secondary ? ' ·' : ''}
                          </span>
                        ) : null}
                        {r.secondary ? <span className="truncate">{r.secondary}</span> : null}
                      </span>
                    ) : null}
                    {/* Why this matched, when it was inside a note rather than the name. */}
                    {r.snippet ? <span className="mt-0.5 block truncate text-[11px] italic text-muted-foreground/80">{r.snippet}</span> : null}
                  </span>
                  <ChevronRight className="size-3 shrink-0 text-muted-foreground" aria-hidden />
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
